import { Component, EventEmitter, Input, Output } from '@angular/core';

import {
  ConnectorAssetRules,
  ConnectorSectionOption
} from '../../connector-control-plane.component';

@Component({
  standalone: false,
  selector: 'app-asset-rule-builder',
  templateUrl: './asset-rule-builder.component.html',
  styleUrls: ['./asset-rule-builder.component.scss']
})
export class AssetRuleBuilderComponent {
  @Input() assetRules!: ConnectorAssetRules;
  @Input() sectionOptions: ConnectorSectionOption[] = [];
  @Output() assetRulesChange = new EventEmitter<ConnectorAssetRules>();

  get groupedSectionOptions(): Array<{ title: string; items: ConnectorSectionOption[] }> {
    const groups = new Map<string, ConnectorSectionOption[]>();
    this.sectionOptions.forEach((section) => {
      const current = groups.get(section.group) || [];
      current.push(section);
      groups.set(section.group, current);
    });
    return Array.from(groups.entries()).map(([title, items]) => ({ title, items }));
  }

  get selectedSectionCount(): number {
    return this.assetRules?.selectedSectionIds?.length || 0;
  }

  get isCustomDateRange(): boolean {
    return this.assetRules?.datePreset === 'custom';
  }

  selectDatePreset(preset: 'all' | 'current-month' | 'last-30-days' | 'custom'): void {
    switch (preset) {
      case 'all':
        this.emitRules({ datePreset: 'all', startDate: null, endDate: null });
        break;
      case 'current-month': {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        this.emitRules({
          datePreset: 'current-month',
          startDate: this.toDateString(firstDay),
          endDate: this.toDateString(lastDay)
        });
        break;
      }
      case 'last-30-days': {
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        this.emitRules({
          datePreset: 'last-30-days',
          startDate: this.toDateString(thirtyDaysAgo),
          endDate: this.toDateString(today)
        });
        break;
      }
      case 'custom':
        this.emitRules({ datePreset: 'custom' });
        break;
    }
  }

  private toDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  toggleReportStatus(reportStatus: 'DRAFT' | 'FINALIZED'): void {
    const current = new Set(this.assetRules?.reportStatuses || []);
    if (current.has(reportStatus)) {
      current.delete(reportStatus);
    } else {
      current.add(reportStatus);
    }
    const next =
      current.size === 0 ? (['FINALIZED'] as Array<'DRAFT' | 'FINALIZED'>) : Array.from(current);
    this.emitRules({ reportStatuses: next });
  }

  updateStartDate(startDate: string): void {
    this.emitRules({ startDate: startDate || null });
  }

  updateEndDate(endDate: string): void {
    this.emitRules({ endDate: endDate || null });
  }

  updateSearchTerm(searchTerm: string): void {
    this.emitRules({ searchTerm });
  }

  toggleSection(sectionId: string): void {
    const selectedIds = new Set(this.assetRules.selectedSectionIds || []);
    if (selectedIds.has(sectionId)) {
      selectedIds.delete(sectionId);
    } else {
      selectedIds.add(sectionId);
    }
    this.emitRules({ selectedSectionIds: Array.from(selectedIds) });
  }

  selectAllSections(): void {
    this.emitRules({ selectedSectionIds: this.sectionOptions.map((section) => section.id) });
  }

  clearAllSections(): void {
    this.emitRules({ selectedSectionIds: [] });
  }

  toggleGroup(groupName: string): void {
    const groupIds = this.sectionOptions
      .filter((section) => section.group === groupName)
      .map((section) => section.id);
    const selectedIds = new Set(this.assetRules.selectedSectionIds || []);
    const allSelected = groupIds.every((id) => selectedIds.has(id));

    if (allSelected) {
      groupIds.forEach((id) => selectedIds.delete(id));
    } else {
      groupIds.forEach((id) => selectedIds.add(id));
    }
    this.emitRules({ selectedSectionIds: Array.from(selectedIds) });
  }

  isGroupSelected(groupName: string): boolean {
    const groupIds = this.sectionOptions
      .filter((section) => section.group === groupName)
      .map((section) => section.id);
    const selectedIds = new Set(this.assetRules?.selectedSectionIds || []);
    return groupIds.every((id) => selectedIds.has(id));
  }

  isGroupIndeterminate(groupName: string): boolean {
    const groupIds = this.sectionOptions
      .filter((section) => section.group === groupName)
      .map((section) => section.id);
    const selectedIds = new Set(this.assetRules?.selectedSectionIds || []);
    const selectedCount = groupIds.filter((id) => selectedIds.has(id)).length;
    return selectedCount > 0 && selectedCount < groupIds.length;
  }

  clearExternalSections(): void {
    this.emitRules({
      selectedSectionIds: this.sectionOptions
        .filter((section) => section.group !== 'Intern')
        .map((section) => section.id)
    });
  }

  isSectionSelected(sectionId: string): boolean {
    return (this.assetRules?.selectedSectionIds || []).includes(sectionId);
  }

  isReportStatusSelected(reportStatus: 'DRAFT' | 'FINALIZED'): boolean {
    return (this.assetRules?.reportStatuses || []).includes(reportStatus);
  }

  clearDateRange(): void {
    this.emitRules({ startDate: null, endDate: null });
  }

  private emitRules(patch: Partial<ConnectorAssetRules>): void {
    this.assetRulesChange.emit({
      ...this.assetRules,
      ...patch
    });
  }
}
